import { ReactNode, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";

interface ButtonProps {
  text?: string;
  icon?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  color?:
    | "purple"
    | "indigo"
    | "teal"
    | "blue"
    | "green"
    | "yellow"
    | "red"
    | "primary";
  variant?: "primary" | "secondary";
  className?: string;
  target?: "_self" | "_blank";
  rel?: ("noopener" | "noreferrer")[];
  link?: string;
  block?: boolean;
  children?: ReactNode;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  iconLeft?: boolean;
  onClick?: () => void;
}

export const Button = ({
  text,
  icon,
  size = "md",
  color = "primary",
  variant = "primary",
  className,
  link,
  target,
  rel,
  block,
  children,
  type = "button",
  disabled = false,
  iconLeft = false,
  onClick,
}: ButtonProps) => {
  const primaryClassname = useMemo(
    () =>
      classNames(
        `flex items-center text-center leading-tight justify-center font-semibold py-2 px-4 max-sm:px-2 bg-${color}-500 border-2 border-${color}-500 text-white rounded-full border-box hover:opacity-90`,
        {
          "w-full": block,
          "w-fit": !block,
          "text-sm": size === "sm",
          "text-base": size === "md",
          "text-lg": size === "lg",
          "text-xl": size === "xl",
          "gap-4": icon,
        }
      ),
    [block, size, icon, color]
  );
  const secondaryClassname = useMemo(
    () =>
      classNames(
        `flex items-center text-center leading-tight justify-center font-semibold py-2 px-4 max-sm:px-2 text-${color}-500 border-2 border-${color}-500 rounded-full border-box hover:bg-gray-100`,
        {
          "w-full": block,
          "w-fit": !block,
          "text-sm": size === "sm",
          "text-base": size === "md",
          "text-lg": size === "lg",
          "text-xl": size === "xl",
          "gap-4": icon,
        }
      ),
    [block, size, icon, color]
  );

  if (link) {
    return (
      <Link
        className={classNames(
          "select-none",
          {
            [`${primaryClassname}`]: variant === "primary",
            [`${secondaryClassname}`]: variant === "secondary",
          },
          className
        )}
        to={link}
        rel={rel && rel.join(", ")}
        target={target}
      >
        {iconLeft && icon}
        {text}
        {children}
        {!iconLeft && icon}
      </Link>
    );
  }

  return (
    <button
      className={classNames(
        "select-none",
        {
          [`${primaryClassname}`]: variant === "primary",
          [`${secondaryClassname}`]: variant === "secondary",
        },
        className
      )}
      disabled={disabled}
      onClick={onClick && onClick}
      type={type}
    >
      {iconLeft && icon}
      {text}
      {children}
      {!iconLeft && icon}
    </button>
  );
};
